import React, {ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import IconButton from '@mui/material/IconButton';
import {AppBar, Autocomplete, Box, Paper, Toolbar} from '@mui/material';
import TextField from '@mui/material/TextField';
import {SearchResult} from '../../utils/canvas/canvas-actions';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import InputAdornment from '@mui/material/InputAdornment';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Stack from '@mui/material/Stack';
import AdminMenu from './menus/Admin.menu';
import EditMenu from './menus/Edit.menu';
import ViewMenu from './menus/View.menu';
import HelpMenu from './menus/Help.menu';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
//import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import {UserContext} from '../../context/userContext';
import {makeStyles} from 'tss-react/mui';
import FormGroup from '@mui/material/FormGroup';
import authService from '../../services/auth.service';
import {useWorkspaceContext} from '../../context/workspaceContext';
import {ResourceType} from '../../@types/resource-type';
import ResourceMenu from './menus/Resource.menu';
import {CanvasActionType} from '../../@types/canvas-action-type';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import * as CanvasUtils from '../../utils/canvas/node-utils';
import {hasRightToEdit} from '../../utils/role-to-rights';
import {canEditComponentDraft} from '../../utils/component-utils';
import Group from '../../@types/group';
import EventMenu from './menus/EventMenu';
import FaultTreeMenu from './menus/FaultTreeMenu';
import FaultTreeNodeMenu from './menus/FaultTreeNodeMenu';
import {getSingularResourceLabel} from '../../utils/resource-utils';
import {uuid} from '../../utils/utils';

import CheckListIcon from '@mui/icons-material/Checklist';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faMessage, faTags, faWandMagicSparkles} from '@fortawesome/free-solid-svg-icons';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {makeUser} from '../../utils/user-utils';
import DescriptionIcon from '@mui/icons-material/Description';

const useStyles = makeStyles()({
    searchTopBarStyle: {
        '& label.Mui-focused': {
            color: 'white',
        },
        '& .MuiInputLabel-root': {
            color: 'white',
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                color: 'white',
                borderColor: '#B2BAC2',
            },
            '&:hover fieldset': {
                color: 'white',
                borderColor: '#B2BAC2',
            },
            '&.Mui-focused fieldset': {
                color: 'white',
                borderColor: '#B2BAC2',
            },
            '& input': {
                color: 'white',
                border: 'none',
            }
        }
    }
});

// Search result styling - defined outside component to avoid recreation on each render
const searchResultStyles = {
    eventBadge: {
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '4px',
        marginRight: '8px',
        fontWeight: 500,
        backgroundColor: '#e3f2fd',
        color: '#1565c0'
    },
    ftNodeBadge: {
        fontSize: '10px',
        padding: '2px 6px',
        borderRadius: '4px',
        marginRight: '8px',
        fontWeight: 500,
        backgroundColor: '#fff3e0',
        color: '#e65100'
    },
    tagChip: {
        fontSize: '10px',
        padding: '1px 6px',
        borderRadius: '12px',
        marginLeft: '4px',
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        border: '1px solid #a5d6a7',
        display: 'inline-block'
    },
    moreTags: {
        fontSize: '10px',
        padding: '1px 6px',
        marginLeft: '4px',
        color: '#666',
        fontStyle: 'italic' as const
    },
    indicator: {
        cursor: 'default',
        fontStyle: 'italic' as const,
        color: '#666',
        backgroundColor: '#f5f5f5',
        pointerEvents: 'none' as const
    }
};
export default function WorkspaceHeader(props: { onBot: () => void, onSash: () => void, onTabClick: Function, addOrSelectResource: any, loadObjects: any }): ReactElement {
    const [searchText, setSearchText] = useState<string>('');
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
    const [searchByTag, setSearchByTag] = useState<boolean>(false);
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);

    // Debounce search text to avoid searching on every keystroke
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 150);

        return (): void => {
            clearTimeout(timer);
        };
    }, [searchText]);
    const { workspaceObjects, currentWorkspaceObject,
        setCurrentCanvasAction, currentCanvasSelectedNode, setCurrentWorkspaceObject,
        canvasSearchFunction, canvasSearchByTagFunction, canvasNavigateFunction, getAdditionalResource } = useWorkspaceContext();
    const { user } = useContext(UserContext);
    const { classes } = useStyles();
    const uuidRef = useRef(uuid());
    const autocompleteRef = useRef<HTMLDivElement>(null);
    const listboxScrollRef = useRef<number>(0);
    const scrollRestoreRafRef = useRef<number | null>(null);

    // Close dropdown when clicking outside the Autocomplete (including its dropdown portal)
    const handleDocumentClick = useCallback((event: MouseEvent): void => {
        if (!isSearchOpen) return;

        const target = event.target as HTMLElement;

        // Check if click is inside the Autocomplete input area
        if (autocompleteRef.current?.contains(target)) return;

        // Check if click is inside the Autocomplete dropdown (rendered in a portal)
        // MUI renders the listbox with role="listbox" or class containing "MuiAutocomplete-popper"
        const isInDropdown = target.closest('.MuiAutocomplete-popper') ||
                            target.closest('[role="listbox"]') ||
                            target.closest('.MuiAutocomplete-option');
        if (isInDropdown) return;

        setIsSearchOpen(false);
    }, [isSearchOpen]);

    useEffect(() => {
        document.addEventListener('mousedown', handleDocumentClick);
        return (): void => {
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [handleDocumentClick]);

    // Cleanup scroll restore RAF on unmount
    useEffect(() => {
        return (): void => {
            if (scrollRestoreRafRef.current !== null) {
                cancelAnimationFrame(scrollRestoreRafRef.current);
                scrollRestoreRafRef.current = null;
            }
        };
    }, []);

    const canEditCanvas = (): boolean => {
        if (!currentWorkspaceObject || currentWorkspaceObject.objectType !== ResourceType.EVENT_SEQUENCE_TREES ) {
            return false;
        }
        return hasRightToEdit(currentWorkspaceObject, undefined);
    }

    // Undo and redo also work on a DRAFT Component's canvas, which has its own edit history.
    const canUndoRedo = (): boolean => {
        if (currentWorkspaceObject?.objectType === ResourceType.COMPONENT_TREE) {
            return canEditComponentDraft(currentWorkspaceObject.object,
                (getAdditionalResource(ResourceType.GROUPS).resources as Group[]) ?? []);
        }
        return canEditCanvas();
    }

    const canDeleteNode = (): boolean => {
        if (!currentCanvasSelectedNode) {
            return false;
        }
        if (CanvasUtils.isFaultTreeNode(currentCanvasSelectedNode.type)) {
            return canEditCanvas();
        }
        return canEditCanvas() && currentCanvasSelectedNode.type !== 'INITIATING';
    }

    // Compute search options when debounced search text changes
    const searchOptions = useMemo((): SearchResult[] => {
        if (debouncedSearchText.length < 2) {
            return [];
        }
        let results: SearchResult[];
        if (searchByTag) {
            results = canvasSearchByTagFunction ? canvasSearchByTagFunction(debouncedSearchText) : [];
        } else {
            results = canvasSearchFunction ? canvasSearchFunction(debouncedSearchText) : [];
        }

        // Add "no results" indicator if no matches found
        if (results.length === 0) {
            return [{
                id: 'no-results',
                uniqueId: '',
                name: 'No results found',
                type: 'indicator'
            }];
        }

        return results;
    }, [debouncedSearchText, searchByTag, canvasSearchFunction, canvasSearchByTagFunction]);

    // Calculate result count for display (excluding indicator items)
    const resultCountInfo = useMemo((): { count: number; hasMore: boolean } => {
        const actualResults = searchOptions.filter(opt => opt.type !== 'indicator');
        const hasMoreIndicator = searchOptions.some(opt => opt.type === 'indicator' && opt.moreCount !== undefined);
        return {
            count: actualResults.length,
            hasMore: hasMoreIndicator
        };
    }, [searchOptions]);

    const handleOptionClick = (event: React.MouseEvent, option: SearchResult): void => {
        event.preventDefault();
        event.stopPropagation();

        // Save listbox scroll position before navigation
        const listbox = document.querySelector('.MuiAutocomplete-listbox');
        if (listbox) {
            listboxScrollRef.current = listbox.scrollTop;
        }

        if (canvasNavigateFunction) {
            canvasNavigateFunction(option.id);
        }

        // Cancel any pending scroll restore RAF
        if (scrollRestoreRafRef.current !== null) {
            cancelAnimationFrame(scrollRestoreRafRef.current);
        }

        // Restore scroll position after navigation triggers re-render
        scrollRestoreRafRef.current = requestAnimationFrame(() => {
            scrollRestoreRafRef.current = null;
            const listboxAfter = document.querySelector('.MuiAutocomplete-listbox');
            if (listboxAfter) {
                listboxAfter.scrollTop = listboxScrollRef.current;
            }
        });
        // Keep dropdown open - don't change isSearchOpen
    };

    const handleSearchClose = (_event: React.SyntheticEvent, reason: string): void => {
        // Close on escape or blur (clicking outside), but not on selectOption
        if (reason === 'escape' || reason === 'blur') {
            setIsSearchOpen(false);
        }
    };

    return (
        <AppBar position="static">
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box display="flex" alignItems="center">
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="Show/Hide Explorer Panel"
                        title='Show/Hide Explorer Panel'
                        onClick={props.onSash}
                    >
                        <VerticalSplitIcon/>
                    </IconButton>
                    <Stack direction="row" style={{ marginLeft: '8px', marginRight: '20px'}}>
                        {authService.isSuperUser() && <AdminMenu onTabClick={props.onTabClick}/>}
                        {currentWorkspaceObject?.objectType !== ResourceType.GROUP_ROLE_PERMISSIONS &&
                            <ResourceMenu label={getSingularResourceLabel(currentWorkspaceObject ? currentWorkspaceObject.objectType : workspaceObjects?.objectType)}
                                addOrSelectResource={props.addOrSelectResource} loadObjects={props.loadObjects} />
                        }
                        {currentWorkspaceObject?.objectType == ResourceType.GROUPS &&
                            <EditMenu />
                        }
                        {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && currentCanvasSelectedNode && CanvasUtils.isEvent(currentCanvasSelectedNode.type) && <EventMenu /> }
                        {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && currentCanvasSelectedNode && CanvasUtils.isEvent(currentCanvasSelectedNode.type) && <FaultTreeMenu /> }
                        {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && currentCanvasSelectedNode && CanvasUtils.isFaultTreeNode(currentCanvasSelectedNode.type) && <FaultTreeNodeMenu /> }
                        {/*{currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && <ReportMenu /> }*/}
                        {currentWorkspaceObject?.objectType == ResourceType.EVENT_SEQUENCE_TREES && <ViewMenu /> }
                        <HelpMenu />
                    </Stack>
                </Box>
                <Box display="flex" >
                    {/* Canvas toolbar - only visible when viewing event sequence trees */}
                    {currentWorkspaceObject?.objectType === ResourceType.EVENT_SEQUENCE_TREES && (
                        <>
                            <Box sx={{ borderLeft: 0.5, borderColor: 'grey.500' }}>
                                <IconButton
                                    color="inherit"
                                    aria-label='center focus'
                                    title='Center Focus'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_RECENTER }) }}
                                >
                                    <CenterFocusStrongIcon  />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='zoom in'
                                    title='Zoom In'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_ZOOM_IN }) }}
                                >
                                    <ZoomInIcon />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='zoom out'
                                    title='Zoom Out'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_ZOOM_OUT }) }}
                                >
                                    <ZoomOutIcon />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='full screen'
                                    title='Full Screen'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_FULLSCREEN }) }}
                                >
                                    <FullscreenIcon  />
                                </IconButton>
                            </Box>
                            <Box sx={{ borderLeft: 0.5, borderColor: 'grey.500' }}>
                                <IconButton
                                    color="inherit"
                                    aria-label='delete'
                                    title='Delete'
                                    onClick={
                                        (): void => {
                                            if (!currentCanvasSelectedNode) return;
                                            let type = currentCanvasSelectedNode.type;
                                            if (CanvasUtils.isFaultTreeNode(type)) {
                                                setCurrentCanvasAction({
                                                    actionType: CanvasActionType.FAULTTREENODE_DELETE_NODE
                                                });
                                            } else {
                                                setCurrentCanvasAction({
                                                    actionType: CanvasActionType.EVENTNODE_DELETE
                                                });
                                            }
                                        }}
                                    disabled={!canDeleteNode()}
                                >
                                    <DeleteIcon />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='redo'
                                    title='Redo'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_REDO }) }}
                                    disabled={!canUndoRedo()}
                                >
                                    <RedoIcon />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='undo'
                                    title='Undo'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_UNDO }) }}
                                    disabled={!canUndoRedo()}
                                >
                                    <UndoIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ borderLeft: 0.5, borderColor: 'grey.500' }}>
                                <IconButton
                                    color="inherit"
                                    aria-label='tags'
                                    title='Toggle tags'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_TAGS }) }}
                                >
                                    <FontAwesomeIcon icon={faTags} />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='callouts'
                                    title='Toggle callouts'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_CALLOUTS }) }}
                                >
                                    <FontAwesomeIcon icon={faMessage} />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='report mapping'
                                    title='Report Mapping'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_TOGGLE_ODNA }) }}
                                >
                                    <CheckListIcon />
                                </IconButton>
                                <IconButton
                                    color="inherit"
                                    aria-label='properties'
                                    title='Properties'
                                    onClick={(): void => { setCurrentCanvasAction({ actionType: CanvasActionType.NODE_PROPERTIES }) }}
                                    disabled={!canEditCanvas() || !currentCanvasSelectedNode}
                                >
                                    <SettingsOutlinedIcon />
                                </IconButton>
                            </Box>
                            <FormGroup sx={{ borderLeft: 0.5, borderColor: 'grey.500', color: 'white' }} ref={autocompleteRef}>
                                <Autocomplete
                                    freeSolo
                                    open={isSearchOpen && debouncedSearchText.length >= 2}
                                    onOpen={(): void => setIsSearchOpen(true)}
                                    onClose={handleSearchClose}
                                    clearOnBlur={false}
                                    blurOnSelect={false}
                                    disableCloseOnSelect
                                    options={searchOptions}
                                    getOptionLabel={(option): string =>
                                        typeof option === 'string' ? option : `${option.uniqueId} - ${option.name}`
                                    }
                                    filterOptions={(x): SearchResult[] => x}
                                    inputValue={searchText}
                                    PaperComponent={({ children, ...props }): ReactElement => (
                                        <Paper {...props}>
                                            {resultCountInfo.count > 0 && (
                                                <Box sx={{
                                                    px: 2,
                                                    py: 1,
                                                    borderBottom: '1px solid #eee',
                                                    color: 'text.secondary',
                                                    fontSize: '12px',
                                                    fontWeight: 500
                                                }}>
                                                    Found {resultCountInfo.hasMore ? '50+' : resultCountInfo.count} match{resultCountInfo.count !== 1 || resultCountInfo.hasMore ? 'es' : ''}
                                                </Box>
                                            )}
                                            {children}
                                        </Paper>
                                    )}
                                    onInputChange={(_event, value, reason): void => {
                                        if (reason !== 'reset') {
                                            setSearchText(value);
                                            if (value.length >= 2) {
                                                setIsSearchOpen(true);
                                            } else {
                                                setIsSearchOpen(false);
                                            }
                                        }
                                    }}
                                    sx={{ ml: 2, mr: 2, mt: 1, width: 300 }}
                                    renderOption={(props, option): ReactElement => {
                                        // Extract onClick and key from props to prevent default behavior and key duplication
                                        const { onClick: _defaultClick, key: _key, ...restProps } = props as React.HTMLAttributes<HTMLLIElement> & { onClick?: React.MouseEventHandler; key?: string };

                                        // Handle indicator type (more results / no results message)
                                        if (option.type === 'indicator') {
                                            return (
                                                <li
                                                    {...restProps}
                                                    key={option.id}
                                                    style={searchResultStyles.indicator}
                                                >
                                                    {option.name}
                                                </li>
                                            );
                                        }

                                        const isEvent = option.type === 'event';
                                        const typeBadgeStyle = isEvent ? searchResultStyles.eventBadge : searchResultStyles.ftNodeBadge;

                                        return (
                                            <li
                                                {...restProps}
                                                key={option.id}
                                                onClick={(e): void => handleOptionClick(e, option)}
                                                style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '8px 16px' }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', flexWrap: 'wrap' }}>
                                                    <Box component="span" sx={typeBadgeStyle}>
                                                        {isEvent ? 'Event' : 'FT Node'}
                                                    </Box>
                                                    <Box component="span" sx={{ fontWeight: 'bold', mr: 1, wordBreak: 'break-word' }} title={option.uniqueId}>
                                                        {option.faultTreeName && option.uniqueId.startsWith(option.faultTreeName)
                                                            ? option.uniqueId.slice(option.faultTreeName.length).trimStart()
                                                            : option.uniqueId}
                                                    </Box>
                                                    <Box component="span" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                                                        {option.name}
                                                    </Box>
                                                </Box>
                                                {option.tags && option.tags.length > 0 && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                                                        {option.tags.map((tag, index) => (
                                                            <Box key={index} component="span" sx={searchResultStyles.tagChip}>
                                                                {tag}
                                                            </Box>
                                                        ))}
                                                        {option.hasMoreTags && (
                                                            <Box component="span" sx={searchResultStyles.moreTags}>
                                                                +{option.hasMoreTags} more
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </li>
                                        );
                                    }}
                                    renderInput={(params): ReactElement => (
                                        <TextField
                                            {...params}
                                            label='Search Fault Tree'
                                            aria-label='search field'
                                            type='text'
                                            variant='outlined'
                                            size='small'
                                            id={`search-box-${uuidRef.current}`}
                                            className={classes.searchTopBarStyle}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    'role': 'searchbox',
                                                    'tabIndex': 0,
                                                    'aria-labelledby': `search-box-${uuidRef.current}`,
                                                    'aria-label': 'search fault tree',
                                                    startAdornment: (
                                                        <>
                                                            <InputAdornment position="start" >
                                                                <IconButton
                                                                    color="inherit"
                                                                    aria-label={searchByTag ? 'Search by tag' : 'Search by node attributes'}
                                                                    title={searchByTag ? 'Search by tag' : 'Search by node attributes'}
                                                                    onClick={(): void => setSearchByTag(!searchByTag)}
                                                                >
                                                                    {searchByTag ? <FontAwesomeIcon icon={faTags} /> : <DescriptionIcon />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                    endAdornment: params.InputProps.endAdornment,
                                                },
                                            }}
                                        />
                                    )}
                                />
                            </FormGroup>
                        </>
                    )}
                    <Box sx={{ borderLeft: 0.5, borderColor: 'grey.500' }}>
                        <IconButton
                            color="inherit"
                            aria-label='chatbot'
                            title='Show/hide chatbot'
                            onClick={props.onBot}
                        >
                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            aria-label='user profile'
                            title='User Profile'
                            onClick={(): void => {
                                setCurrentWorkspaceObject({ objectType: ResourceType.USERS, object: makeUser(user) });
                            }}
                        >
                            <AccountCircleIcon />
                        </IconButton>
                    </Box>

                </Box>
            </Toolbar>
        </AppBar>

    );
}
